<template>
  <div>
    <!-- page meta -->
    <Head>
      <title>Consulting CRM Software by Platforma</title>
      <link
        rel="icon"
        :href="$filters.public_path('favicon.png')"
        type="favicon"
      />
      <meta
        name="description"
        content="Provide your clients with their own personal login to manage their accounts with you."
      />
    </Head>
  </div>

  <!-- content start -->

  <div
    class="min-[1140px]:max-w-[1140px] container relative mx-auto bg-transparent"
  >
    <!-- ===================== HERO SECTION ===================== -->

    <section
      id="section1"
      class="hero-section relative mx-auto mt-[150px] justify-center gap-1 bg-transparent lg:flex lg:py-0"
      :style="backgroundImageStyleMain"
    >
      <div class="mt-0 bg-transparent px-[25px] xl:px-0">
        <h1
          class="top-full mt-0 px-0 text-[35px] font-extrabold tracking-normal text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
        >
          Give businesses on retainer their own portal.
        </h1>

        <p
          class="mt-[5px] mb-[25px] text-[15px] font-medium text-[#7c828d] lg:text-lg"
        >
          Lock in clients by providing them with something tangible. Their own
          one-stop shop login to your ongoing support.
        </p>

        <!-- captcher email widget -->

        <EmailCaptureHeaderWidget />

        <img :src="$filters.media_path('/images/stars.svg')" alt="" />
      </div>

      <div class="mb-[30px] mt-[30px] lg:mt-0 z-10">
        <img
          loading="lazy"
          class="lg:max-w-[650px]"
          :src="
            $filters.media_path(
              '/images/private-client-portal-video-calls-and-more.png'
            )
          "
          alt="Provide a private member portal with video calls."
        />
      </div>
    </section>

    <div
      class="join-mobile mx-auto mt-[30px] h-[150px] max-w-6xl gap-2 bg-[#f5f8fa] py-[65px] md:mt-[150px] md:flex md:h-auto md:bg-[#fafbfc] md:bg-blend-overlay"
    >
      <div class="m-auto hidden h-[1px] w-[400px] bg-[#e9ebf0] md:flex"></div>
      <h2
        class="mx-auto px-0 text-center text-[12px] font-[500] text-[#7c828d]"
      >
        JOIN 10,000+ HIGH EARNING BUSINESSES & CREATORS
      </h2>
      <div class="m-auto hidden h-[1px] w-[400px] bg-[#e9ebf0] md:flex"></div>
    </div>

    <!-- ===================== TRAINING SECTION===================== -->
    <section
      class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[50px] lg:flex lg:h-[500px] xl:px-0"
    >
      <img
        class="absolute top-10 left-[600px] z-0 hidden h-[600px] w-[810px] origin-top-right lg:block"
        :src="$filters.media_path('/images/blue_gradient.svg')"
        alt=""
      />

      <div class="z-20 mx-auto mt-12 justify-center gap-1 py-0 lg:flex">
        <div class="mt-0 text-center lg:max-w-[500px] lg:text-left">
          <div id="text_training" class="text-xs font-extrabold text-[#2D8CFF]">
            TRAINING
          </div>

          <h2
            class="top-full mt-[20px] px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
          >
            All your training material in one place.
          </h2>

          <hr
            class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#2D8CFF] lg:ml-0"
          />

          <p
            class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
          >
            With a simple log in, customers can consume all your training
            material in form of courses or video libraries, and even podcasts.
          </p>
        </div>

        <div class="z-30 mt-[25px] xl:mt-0">
          <img
            loading="lazy"
            class="lg:w-[675px]"
            :src="
              $filters.media_path(
                '/images/online-training-courses-for-members.png'
              )
            "
            alt="Host all your company training courses in one place."
          />
        </div>
      </div>
    </section>

    <!-- ===================== DIRECT LINE OF COMMUNICATION SECTION ===================== -->
    <section
      class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[200px] lg:flex lg:h-[500px] lg:flex-row-reverse xl:px-0"
    >
      <img
        class="absolute top-0 right-[600px] z-0 h-[600px] w-[810px] origin-top-left overflow-hidden"
        :src="$filters.media_path('/images/green-diamond-dots-bg.svg')"
        alt=""
      />

      <div
        class="mt-12 text-center lg:mx-0 lg:mt-0 lg:max-w-[500px] lg:px-[30px] lg:text-left"
      >
        <div
          id="text_direct_line"
          class="text-xs font-extrabold text-[#27AE60] lg:left-0"
        >
          DIRECT LINE OF COMMUNICATION
        </div>

        <h2
          class="top-full px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] md:mt-[20px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
        >
          Give clients the feel of you being on call.
        </h2>

        <div class="">
          <hr
            class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#27AE60] lg:ml-0"
          />
        </div>

        <p
          class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
        >
          An advanced chat without the hassle. The entire Rolodex and one hub of
          contact to live chat, conduct video calls, share documents and more.
        </p>
      </div>

      <div
        class="mx-auto mt-12 justify-center gap-1 py-0 md:z-10 lg:mt-0 lg:px-0"
      >
        <video
          loop
          muted
          autoplay
          class="lazy rounded-3xl shadow-lg"
          id="home_video"
        >
          <source
            :src="
              $filters.media_path('/videos/CHAT CREATOR UI WITH MUSIC 16_9.mp4')
            "
            type="video/mp4"
          />
        </video>
      </div>
    </section>

    <!-- ===================== BREAKING NEWS SECTION ===================== -->
    <section
      class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[150px] lg:flex lg:h-[700px] xl:px-0"
    >
      <img
        class="absolute top-10 left-[600px] z-0 hidden h-[600px] w-[810px] origin-top-right lg:block"
        :src="$filters.media_path('/images/platforma_turqoise_diamond_bg.svg')"
        alt=""
      />

      <div class="z-20 mx-auto mt-12 justify-center gap-1 py-0 lg:flex">
        <div class="mt-0 text-center lg:max-w-[500px] lg:text-left">
          <div
            id="text_breaking_news"
            class="text-xs font-extrabold text-[#49CCF9]"
          >
            BREAKING NEWS
          </div>

          <h2
            class="top-full mt-[20px] px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
          >
            Keep the entire book of business up to date.
          </h2>

          <hr
            class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#49CCF9] lg:ml-0"
          />

          <p
            class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
          >
            Mass broadcast or post in your private social newsfeed everything
            going on with your business.<br />
            Keep everyone in the loop for upcoming live or virtual events, new
            services, or changes.
          </p>
        </div>

        <div class="z-30 mt-[25px] xl:mt-0">
          <video
            class="lg:max-w-[648px] rounded-[10px] lg:rounded-[20px] shadow-2xl"
            id=""
            loop
            muted
            autoplay
            playsinline
          >
            <source
              :src="
                $filters.media_path(
                  '/videos/social-group-for-marketing-in-course.mp4'
                )
              "
              type="video/mp4"
            />
          </video>
        </div>
      </div>
    </section>
  </div>
  <!-- ===================== CTA BLOCK ===================== -->
  <CtaBlock cta-headline="Collaborate B2B on another level." />
</template>

<!-- =======CONTENT END======== -->

<script>
import DefaultLayout from '../Layouts/DefaultLayout.vue';
import ButtonComponent from '@shared/Components/ButtonComponent.vue';
import Axios from 'axios';
import EmailCaptureHeaderWidget from '../Components/EmailCaptureHeader.vue';
import CtaBlock from '../Components/CtaBlockFooterComponent.vue';

export default {
  layout: DefaultLayout,
  components: {
    ButtonComponent,
    EmailCaptureHeaderWidget,
    CtaBlock
  },

  computed: {
    backgroundImageStyleMain() {
      const imagePath = this.$filters.media_path(
        'images/platforma-header-background-eclipse.svg'
      );
      return {
        '--before-background-image': `url(${imagePath})`
      };
    }
  }
};
</script>

<style scoped>
.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  height: 1000px;
  width: 1700px;
  display: block;
  z-index: 0;
  transform: translateY(-42%) translateX(58%);
  background-repeat: no-repeat;
  background-image: var(--before-background-image);
}

.join-mobile {
  background-image: url(../media/images/two-dots.svg);
  background-size: contain;
}
</style>
